<template>
  <div class="services-page">
    <Header />
    <main class="container mx-auto py-6 pb-16">
      <!-- 面包屑导航 -->
      <div class="mb-6">
        <div class="flex items-center text-sm bg-gray-50 px-4 py-3 rounded-lg">
          <router-link to="/" class="text-gray-500 hover:text-blue-600">网站首页</router-link>
          <span class="mx-2 text-gray-400">/</span>
          <router-link to="/services" class="text-gray-500 hover:text-blue-600">服务项目</router-link>
          <span class="mx-2 text-gray-400">/</span>
          <span class="text-blue-600 font-medium">{{ categories[activeCategory] }}</span>
        </div>
      </div>

      <div class="flex flex-col lg:flex-row gap-8">
        <!-- 左侧分类 -->
        <div class="lg:w-1/4">
          <div class="card-base p-6">
            <h3 class="text-xl font-bold mb-6 text-gray-800">服务分类</h3>
            <div class="space-y-2">
              <div
                v-for="(category, index) in categories"
                :key="index"
                class="category-item py-3 px-4 rounded-lg font-medium transition-all duration-300 hover:bg-gray-100 cursor-pointer"
                :class="{ active: activeCategory === index }"
                @click="activeCategory = index"
              >
                {{ category }}
              </div>
            </div>
          </div>
        </div>

        <!-- 右侧服务列表 -->
        <div class="lg:w-3/4 min-h-[400px]">
          <!-- 服务项目列表 -->
          <div class="space-y-6 h-full">
            <div v-if="services.length === 0" class="flex items-center justify-center h-full bg-white rounded-lg shadow-sm">
              <p class="text-gray-500">暂无数据</p>
            </div>
            <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div
                v-for="(service, index) in services"
                :key="index"
                class="service-item card-base overflow-hidden transition-all duration-300 hover:translate-y-[-4px] hover:shadow-lg hover:border-blue-500"
              >
                <div class="h-48 overflow-hidden">
                  <img
                    :src="getFullImageUrl(service.image)"
                    :alt="service.title"
                    class="service-image w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                    @error="handleImageError"
                  >
                </div>
                <div class="p-6">
                  <h3 class="text-xl font-bold mb-3 text-gray-800">{{ service.title }}</h3>
                  <p class="text-gray-600 mb-4 line-clamp-3">{{ service.description }}</p>
                  <router-link
                    :to="'/article/' + service.id"
                    class="text-blue-600 font-medium hover:text-blue-800 flex items-center"
                  >
                    详情 <i class="fa fa-arrow-right ml-2"></i>
                  </router-link>
                </div>
              </div>
            </div>
            <!-- 分页 -->
            <div v-if="services.length > 0" class="flex justify-center mt-12">
              <div class="flex items-center space-x-2">
                <button
                  class="pagination-item py-2 px-4 rounded-lg transition-all duration-300 hover:bg-gray-100"
                  :class="{ 'text-gray-400 cursor-not-allowed': currentPage === 1 }"
                  :disabled="currentPage === 1"
                  @click="prevPage"
                >
                  <i class="fa fa-chevron-left mr-1"></i> 上一页
                </button>
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  class="pagination-item py-2 px-4 rounded-lg transition-all duration-300 hover:bg-gray-100"
                  :class="page === '...' ? 'cursor-default border-none bg-transparent' : (currentPage === page ? 'bg-blue-600 text-white' : '')"
                  :disabled="page === '...'"
                  @click="page !== '...' && (currentPage = page)"
                >
                  {{ page }}
                </button>
                <button
                  class="pagination-item py-2 px-4 rounded-lg transition-all duration-300 hover:bg-gray-100"
                  :class="{ 'text-gray-400 cursor-not-allowed': currentPage === totalPages }"
                  :disabled="currentPage === totalPages"
                  @click="nextPage"
                >
                  下一页 <i class="fa fa-chevron-right ml-1"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import Header from '../components/Header.vue'
import AppFooter from '../components/Footer.vue'
import { api } from '../api'
import { CURRENT_CONFIG } from '../api/config';

export default {
  name: 'ServicesPage',
  components: {
    Header,
    AppFooter
  },
  data() {
    return {
      categories: [],
      categoryObjects: [],
      activeCategory: 0,
      services: [],
      currentPage: 1,
      pageSize: 8,
      totalPages: 1,
      loading: false,
      BASE_URL: CURRENT_CONFIG.BASE_URL
    }
  },
  async mounted() {
    await this.loadCategories()
    this.setActiveCategoryFromRoute()
  },
  watch: {
    activeCategory() {
      this.currentPage = 1
      this.loadServices()
    },
    currentPage() {
      this.loadServices()
    },
    '$route.query.category'() {
      this.setActiveCategoryFromRoute()
    }
  },
  computed: {
    visiblePages() {
      const total = this.totalPages
      const current = this.currentPage
      const maxVisible = 5
      if (total <= maxVisible) {
        return Array.from({ length: total }, (_, i) => i + 1)
      }
      const pages = []
      if (current <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      } else if (current >= total - 2) {
        pages.push(1)
        pages.push('...')
        for (let i = total - 3; i <= total; i++) pages.push(i)
      } else {
        pages.push(1)
        pages.push('...')
        for (let i = current - 1; i <= current + 1; i++) pages.push(i)
        pages.push('...')
        pages.push(total)
      }
      return pages
    }
  },
  methods: {
    async loadCategories() {
      try {
        this.loading = true
        const categories = await api.getCategories()
        const serviceCategories = categories.filter(cat => cat.firstLevelModule === '服务项目')
        this.categoryObjects = serviceCategories
        this.categories = serviceCategories.map(cat => cat.secondLevelModule)
        if (this.categories.length > 0) {
          await this.loadServices()
        }
      } catch (error) {
        console.error('加载分类失败:', error)
      } finally {
        this.loading = false
      }
    },
    async loadServices() {
      try {
        this.loading = true
        if (this.categoryObjects.length === 0 || this.activeCategory >= this.categoryObjects.length) {
          this.services = []
          return
        }

        const categoryId = this.categoryObjects[this.activeCategory].id
        const result = await api.getArticles(categoryId, '', this.currentPage, this.pageSize)
        this.services = result.rows.map(article => ({
          id: article.id,
          title: article.title || '暂无标题',
          description: article.summary || article.content?.substring(0, 100) + '...' || '暂无描述',
          image: article.coverImageUrl || `https://picsum.photos/seed/${article.id}/500/300`
        }))

        const total = result.total || 0
        this.totalPages = Math.ceil(total / this.pageSize)
      } catch (error) {
        console.error('加载服务列表失败:', error)
        this.services = []
      } finally {
        this.loading = false
      }
    },
    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--
      }
    },
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++
      }
    },
    setActiveCategoryFromRoute() {
      const categoryFromRoute = this.$route.query.category
      if (categoryFromRoute) {
        const categoryIndex = this.categories.findIndex(cat => cat === categoryFromRoute)
        if (categoryIndex !== -1) {
          this.activeCategory = categoryIndex
        }
      }
    },
    getFullImageUrl(path) {
      if (!path) return require('../assets/default_logo.jpg')
      if (path.startsWith('http')) return path
      return this.BASE_URL + path
    },
    handleImageError(event) {
      event.target.src = require('../assets/default_logo.jpg')
    }
  }
}
</script>

<style scoped>
.services-page {
  font-family: var(--font-family);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: var(--color-gray-700);
  margin: 0;
  min-height: 100vh;
}

/* 服务分类样式 */
.category-item.active {
  background: linear-gradient(135deg, #4f7fe6 0%, #3b82f6 100%);
  color: white;
}

/* 分页样式 */
.pagination-item.active {
  background: linear-gradient(135deg, #4f7fe6 0%, #3b82f6 100%);
  color: white;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .services-page main {
    padding: 1rem;
  }

  .category-item {
    text-align: center;
  }

  .pagination-item {
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
  }
}
</style>
